import React, { FC, useState, ReactElement } from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { Colors } from "../../styles";
import { PreviousIcon, NextIcon } from "./icons/Icons";
import moment, { Moment } from "moment";
import "moment/locale/de";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: "288px",
        },
        header: {
            display: "flex",
            flexFlow: "row",
            alignItems: "center",

            paddingLeft: 16,
            paddingRight: 16,
            height: 40,

            backgroundColor: Colors.anthracite,
        },
        headerPreviousIcon: { padding: 0, margin: 0 },
        headerNextIcon: { padding: 0, margin: 0 },
        headerTitle: {
            flex: 1,

            margin: 0,

            textAlign: "center",
            color: "#fff",
            fontFamily: "innogyWEB",
            fontSize: 16,
        },
        content: {
            paddingLeft: 8,
            paddingRight: 8,

            backgroundColor: Colors.white,
        },
        contentContainer: {
            marginTop: 20,
            marginBottom: 20,
        },
        headerRow: {
            height: 35,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
        },
        headerCW: {
            flex: 1,
            textAlign: "center",
            fontFamily: "innogyWEB",
            fontSize: 14,
            fontWeight: 500,
            color: Colors.mellowYellow,
        },
        headerWeekday: {
            flex: 1,
            textAlign: "center",
            fontFamily: "innogyWEB",
            fontSize: 14,
            fontWeight: 300,
            color: Colors.mellowYellow,
        },
        itemRow: {
            margin: 1,
            height: 35,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            borderRadius: 35 / 2,
        },
        itemRowHighlighted: {
            backgroundColor: "orange",
        },
        item: {
            flex: 1,
            textAlign: "center",
            fontFamily: "innogyWEB",
            fontSize: 16,
            fontWeight: 300,
        },
        itemCalendarWeek: {
            color: Colors.mellowYellow,
            fontWeight: 500,
        },
        itemCalendarWeekMarked: {
            color: Colors.white,
            fontWeight: 500,
        },
        itemDayOfOtherMonth: {
            color: Colors.anthracite25,
        },
        itemDayOfOtherMonthMarked: {
            color: Colors.anthracite25,
        },
        itemDayOfMonth: {
            color: Colors.anthracite,
        },
        itemDayOfMonthMarked: {
            color: Colors.white,
        },
        itemDayOfMonthNotSelectable: {
            color: Colors.anthracite25,
        },
    }),
);

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props {
    firstDay: Moment;
    resultCallback(restult: Moment): void;
    isCurrentWeekSelectable: boolean;
}

const getMonthString = (date: Moment): string =>
    date
        .locale("de")
        .format("MMMM")
        .toUpperCase();

const getYearString = (date: Moment): string => date.locale("de").format("YYYY");

export const CalendarWeekChooser: FC<Props> = ({
    firstDay,
    isCurrentWeekSelectable,
    resultCallback,
}: Props) => {
    const classes = useStyles();
    const [currentDate, setCurrentDate] = useState(firstDay.clone().startOf("month"));
    const [currentRow, setCurrentRow] = useState(-1);
    let firstDateInRow: Moment[] = [];

    const contentArray = ((firstDayOfMonth: Moment): ReactElement[] => {
        const endOfMonth = firstDayOfMonth.clone().date(firstDayOfMonth.daysInMonth());
        // eslint-disable-next-line react/jsx-key
        const content = [];

        const topLeftDate = ((date: Moment): Moment => {
            const dayInWeek = date.day();
            const difference = date.date() - dayInWeek + (dayInWeek === 0 ? -6 : 1);
            return date.clone().date(difference);
        })(firstDayOfMonth);

        const bottomRightDate = ((date: Moment): Moment => {
            const dayInWeek = date.day();
            const missing = 7 - (dayInWeek === 0 ? 7 : dayInWeek);
            return date.clone().date(date.date() + missing);
        })(endOfMonth);

        let loop = topLeftDate.clone();
        let dayCounter = 0;
        let cnt = 0;
        firstDateInRow = [];

        while (loop <= bottomRightDate) {
            const marked =
                currentRow === Math.floor(cnt / 8) &&
                loop
                    .clone()
                    .weekday(isCurrentWeekSelectable ? 7 : 0)
                    .isAfter(firstDay);
            const classCalendarWeek = marked
                ? classes.itemCalendarWeekMarked
                : classes.itemCalendarWeek;

            const classItem =
                // prettier-ignore-start
                loop.month() === firstDayOfMonth.month() //   aktueller Monat?
                    ? marked //                             aktuelle Zeile markiert?
                        ? classes.itemDayOfMonthMarked //   dann ...MonthMarked style
                        : loop
                              .clone()
                              .weekday(isCurrentWeekSelectable ? 7 : 0)
                              .isBefore(firstDay)
                        ? classes.itemDayOfMonthNotSelectable
                        : classes.itemDayOfMonth //         sonst...Month style
                    : marked //                             nicht aktueller Monat aber markiert?
                    ? classes.itemDayOfOtherMonthMarked //  dann ...OtherMonthMarked style
                    : classes.itemDayOfOtherMonth; //       sonst...OterMonth style
            // prettier-ignore-end

            const day = moment(loop);
            // Wochenbeginn? Einfügen der KW und firstDateInRow setzen
            if (dayCounter++ % 7 === 0) {
                firstDateInRow.push(loop.clone());
                content.push(
                    <p className={[classes.item, classCalendarWeek].join(" ")} key={cnt++}>
                        {day.format("w")}
                    </p>,
                );
            }
            // Tag einfügen
            content.push(
                <p className={[classes.item, classItem].join(" ")} key={cnt++}>
                    {day.format("D")}
                </p>,
            );
            loop = loop.add(1, "day");
        }

        return content;
    })(currentDate);

    const nextMonth = (): void => {
        setCurrentRow(-1);
        setCurrentDate(
            currentDate
                .clone()
                .add(1, "month")
                .startOf("month"),
        );
    };
    const previousMonth = (): void => {
        setCurrentRow(-1);
        setCurrentDate(
            currentDate
                .clone()
                .subtract(1, "month")
                .startOf("month"),
        );
    };
    const resetToFirstDay = (): void => {
        setCurrentRow(-1);
        setCurrentDate(firstDay.clone().startOf("month"));
    };

    const clickOnRow = (index: number): void => {
        const newRow = index / 8;
        if (firstDateInRow[newRow].weekday(isCurrentWeekSelectable ? 7 : 0).isBefore(firstDay))
            return;

        const date = firstDateInRow[newRow].add(1, "day");
        resultCallback(date);
    };

    const weekDays = ["MO", "DI", "MI", "DO", "FR", "SA", "SO"];

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <IconButton className={classes.headerPreviousIcon} onClick={previousMonth}>
                    <PreviousIcon />
                </IconButton>
                <p className={classes.headerTitle} onClick={resetToFirstDay}>
                    {getMonthString(currentDate)} {getYearString(currentDate)}
                </p>
                <IconButton className={classes.headerNextIcon} onClick={nextMonth}>
                    <NextIcon />
                </IconButton>
            </div>
            <div className={classes.content}>
                <div className={classes.contentContainer}>
                    <div className={classes.headerRow}>
                        <p className={classes.headerCW}>KW</p>
                        {weekDays.map(day => {
                            return (
                                <p className={classes.headerWeekday} key={day}>
                                    {day}
                                </p>
                            );
                        })}
                    </div>
                    {contentArray.map((element, index) => {
                        if (index % 8 === 0) {
                            return (
                                <div
                                    className={[
                                        classes.itemRow,
                                        currentRow === (index > 0 ? index / 8 : 0) &&
                                        firstDateInRow[index / 8]
                                            .weekday(isCurrentWeekSelectable ? 7 : 0)
                                            .isAfter(firstDay)
                                            ? classes.itemRowHighlighted
                                            : "",
                                    ].join(" ")}
                                    key={index}
                                    onClick={() => clickOnRow(index)}
                                    onMouseOver={() => {
                                        setCurrentRow(index / 8);
                                    }}
                                >
                                    {contentArray.slice(index, index + 8)}
                                </div>
                            );
                        } else {
                            return null;
                        }
                    })}
                </div>
            </div>
        </div>
    );
};
